import React, { useCallback, useMemo, useState } from 'react';

import classnames from 'classnames';

import LinkButton, { PrimaryButton, SecondaryButton } from 'components/uiLibrary/LinkButton';
import Modal from 'components/uiLibrary/Modal';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import useTracking from 'components/Globals/Analytics';
import { COMPONENTS, SUB_COMPONENTS, GOOGLE_OLD_TRACKING_SERVICES } from 'components/Globals/Analytics/constants';
import CastingToolPromo from 'components/Globals/CastingToolPromo';
import ShareEntity from 'components/Globals/ShareEntity';

import { TP, PERFORMANCE_DATE_MODE_TYPES, OUTBOUND_LINK_TYPES } from 'constants/index';

import { createDate } from 'utils/date';
import useAppContext from 'utils/hooks/useAppContext';
import { useDialogs } from 'utils/hooks/useDialogs';
import { getTicketsURL } from 'utils/productions';
import { trackClickTickets } from 'utils/tracking';

import { currencySymbols } from 'containers/Payment/constants';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES } from 'operabase-router/constants';

import classes from './Actions.module.scss';

const SecondaryActions = ({
  entity,
  trackingData,
  entityType,
  isTextButton,
  hasFuturePerformancesDates,
  actionType,
  data,
  variant,
}) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { isLoggedIn } = useAppContext();
  const { setIsLoginDialog } = useDialogs();
  const [isOpen, setIsOpen] = useState(false);
  const track = useTracking();

  const onCloseHandler = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(false);
  };

  // NOTE: This We will enable if product Conforms
  // const handleOpen = e => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   setIsOpen(true);
  // };

  const hasWatchOptions = useMemo(
    () =>
      data?.attributes?.[0]?.url ||
      [PERFORMANCE_DATE_MODE_TYPES.FULL_VIDEO, PERFORMANCE_DATE_MODE_TYPES.LIVESTREAM].includes(data?.mode) ||
      data?.stats?.media?.videos?.digital?.exists ||
      data?.performances?.some(performance =>
        [PERFORMANCE_DATE_MODE_TYPES.FULL_VIDEO, PERFORMANCE_DATE_MODE_TYPES.LIVESTREAM].includes(performance?.mode),
      ),
    [data],
  );

  const ticketUrl = useMemo(() => getTicketsURL(data), [data]);
  const { tickets } = data || {};
  const currencySymbol = currencySymbols[tickets?.[0]?.currency?.currency] || {};
  const ticketPrice = `${currencySymbol ? `${currencySymbol}` : ''}${Number(tickets?.[0]?.minTicketPrice)} - ${
    currencySymbol ? `${currencySymbol}` : ''
  }${Number(tickets?.[0]?.maxTicketPrice)}`;

  const trackTickets = useCallback(() => {
    track.click(trackClickTickets({ 'Click URL': ticketUrl, production_id: data?.id }), GOOGLE_OLD_TRACKING_SERVICES);
  }, [track, ticketUrl, data]);

  const getButtonContent = () => {
    switch (actionType) {
      case 'tickets':
        return {
          text: t(`${TP}.FN_TICKETS`),
          leftIcon: null,
          rightIcon: <SpriteIcon icon="more_vert" size={14} />,
          price: tickets?.[0]?.minTicketPrice || tickets?.[0]?.maxTicketPrice ? `(${ticketPrice})` : null,
        };
      case 'watch':
        return {
          text: variant === 'textAndBlock' ? t(`${TP}.m_WATCH`) : t(`${TP}.FN_WATCH_ONLINE_VIDEOS`),
          leftIcon: <SpriteIcon icon="play_circle_outline" size={14} />,
          rightIcon: <SpriteIcon icon="more_vert" size={14} />,
          price: null,
        };
      case 'options':
      default:
        return {
          text: t(`${TP}.FN_OPTIONS`),
          leftIcon: null,
          rightIcon: null,
          price: null,
        };
    }
  };

  const buttonContent = getButtonContent();

  const renderButton = () => {
    if (isTextButton) {
      return (
        <LinkButton
          variant="text"
          size="small"
          stopPropagation
          trackingData={{
            ...trackingData,
            component: actionType === 'tickets' ? COMPONENTS.TICKET_CTA : COMPONENTS.WATCH_OPTIONS_CTA,
          }}
        >
          <Typography size={11} weight="bold" className={classes.optionsText}>
            {buttonContent.text}
            {actionType === 'tickets' && buttonContent.price && (
              <Typography size={11} color="secondary" className={classes.textButton__ticketPrice} truncate>
                {buttonContent.price}
              </Typography>
            )}
            {actionType === 'watch' && <SpriteIcon icon="chevron_right" size={14} />}
            {actionType === 'options' && <SpriteIcon icon="more_vert" size={14} />}
          </Typography>
        </LinkButton>
      );
    }

    if (variant === 'textAndBlock') {
      return (
        <LinkButton
          variant="text"
          size="small"
          styles={{
            root: classnames(classes.textAndBlockBtn, { [classes.textAndBlockBtnOption]: actionType === 'options' }),
          }}
          trackingData={{
            ...trackingData,
            component: COMPONENTS.OPTIONS_CTA,
          }}
        >
          <Typography size={12} weight="medium" className={classes.optionsText}>
            {buttonContent.text}
            {actionType === 'tickets' && typeof currencySymbol === 'string' && (
              <Typography size={12} weight="medium" className={classes.textAndBlockBtn__ticketPrice} truncate>
                {currencySymbol}
              </Typography>
            )}
            {actionType === 'options' && (
              <SpriteIcon icon="share" size={14} className={classes.textAndBlockBtn__shareIcon} />
            )}
            {actionType === 'watch' && (
              <SpriteIcon icon="play_circle_outline" size={14} className={classes.textAndBlockBtn__playIcon} />
            )}
            {['watch', 'tickets'].includes(actionType) && (
              <SpriteIcon icon="more_vert" size={14} className={classes.textAndBlockBtn__moreIcon} />
            )}
          </Typography>
        </LinkButton>
      );
    }

    return (
      <div className={classes.actions__secondary}>
        {actionType === 'tickets' && (
          <PrimaryButton
            shape="rectangle"
            leftIcon={buttonContent.leftIcon}
            rightIcon={buttonContent.rightIcon}
            styles={{ root: classes.ticketBtn }}
            size="medium"
            trackingData={{
              ...trackingData,
              component: COMPONENTS.TICKET_CTA,
            }}
          >
            {buttonContent.text}
            {buttonContent.price && (
              <Typography size={11} color="secondary" italic className={classes.ticketPrice} truncate>
                {buttonContent.price}
              </Typography>
            )}
          </PrimaryButton>
        )}
        {actionType === 'watch' && (
          <PrimaryButton
            shape="rectangle"
            leftIcon={buttonContent.leftIcon}
            rightIcon={buttonContent.rightIcon}
            styles={{ root: classes.watchOnlineBtn }}
            size="medium"
            trackingData={{
              ...trackingData,
              subComponent: SUB_COMPONENTS.WATCH_OPTIONS_CTA,
            }}
          >
            <Typography size={12} weight="medium" color="white" className={classes.watchOnlineBtn__text} truncate>
              {buttonContent.text}
            </Typography>
          </PrimaryButton>
        )}
        {actionType === 'options' && (
          <SecondaryButton
            shape="rectangle"
            styles={{ root: classes.otherOptionBtn }}
            trackingData={{
              ...trackingData,
              component: COMPONENTS.OPTIONS_CTA,
            }}
          >
            <Typography size={12} weight="bold" color="blue">
              {buttonContent.text}
            </Typography>
            {hasFuturePerformancesDates && <SpriteIcon icon="notifications" size={14} className={classes.icon} />}
            <SpriteIcon icon="share" size={14} className={classes.icon} />
          </SecondaryButton>
        )}
      </div>
    );
  };

  return (
    <>
      {renderButton()}
      <Modal
        allowMobileDrawer
        isOpen={isOpen}
        onClose={onCloseHandler}
        title={t(`${TP}.FN_OPTIONS`)}
        styles={{ title: classes.modalTitle }}
      >
        <div className={classes.actions__secondary_drawer}>
          {ticketUrl && (
            <div className={classes.listItem}>
              <Typography weight="bold">{t(`${TP}.FN_TICKETS`)}</Typography>
              <div className={classes.listItem__borderWrapper}>
                <LinkButton
                  variant="text"
                  size="small"
                  isLink
                  href={ticketUrl}
                  external
                  externalLinkType={OUTBOUND_LINK_TYPES.TICKET}
                  stopPropagation
                  onClick={trackTickets}
                  rightIcon={<SpriteIcon icon="chevron_right" size={14} />}
                  styles={{ root: classes.modalBtn }}
                >
                  {t(`${TP}.FN_TICKETS`)}
                  {buttonContent.price && (
                    <Typography size={11} className={classes.textButton__ticketPrice} truncate>
                      {buttonContent.price}
                    </Typography>
                  )}
                </LinkButton>
              </div>
            </div>
          )}

          {hasWatchOptions && (
            <div className={classes.listItem} onClick={onCloseHandler}>
              <Typography weight="bold">{t(`${TP}.FN_WATCH_ONLINE_VIDEOS`)}</Typography>
              <div className={classes.listItem__borderWrapper}>
                <SpriteIcon icon="play_circle_outline" size={16} className={classes.iconDrawer} />
                <Typography weight="medium" size={12}>
                  {t(`${TP}.FN_WATCH_ONLINE_VIDEOS`)}
                </Typography>
              </div>
            </div>
          )}

          <div className={classes.listItem}>
            <Typography weight="bold">{t(`${TP}.FN_SHARE_PAGE`)}</Typography>
            <ShareEntity
              entityType={ENTITY_TYPES.PROFILE}
              entity={entity}
              trackingData={{
                ...trackingData,
                component: COMPONENTS.SHARE_CTA,
              }}
              className={classes.shareEntity}
              styles={{ label: classes.shareEntityLabel }}
            />
          </div>

          {entityType === ENTITY_TYPES.PROFILE && entity?.id && (
            <div className={classes.listItem}>
              <Typography weight="bold" color="blue">
                {t(`${TP}.FN_CASTINGTOOL`)}
              </Typography>
              <div className={classes.rightSide__castingToolPromo}>
                <CastingToolPromo
                  entityType={entityType}
                  entity={entity}
                  asCTA
                  trackingData={{
                    ...trackingData,
                    component: COMPONENTS.VIEW_IN_CASTING_TOOL_CTA,
                  }}
                  styles={{ castingToolCta: classes.castingToolCta }}
                />
              </div>
            </div>
          )}

          {!isLoggedIn && (
            <div className={classes.listItem}>
              <Typography weight="bold" color="blue">
                {t(`${TP}.m_LOGIN`)}
              </Typography>
              <PrimaryButton
                onClick={() => setIsLoginDialog({ isOpen: true })}
                shape="rectangle"
                leftIcon={<SpriteIcon icon="account_circle" size={14} />}
                styles={{ icon: classes.accountIcon, root: classes.loginBtn }}
                size="medium"
                trackingData={{
                  ...trackingData,
                  subComponent: SUB_COMPONENTS.LOGIN_CTA,
                }}
              >
                {t(`${TP}.m_LOGIN`)}
              </PrimaryButton>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

const Actions = ({ data, entity, entityType, trackingData, isTextButton = false, variant }) => {
  const productionMaxDate = createDate(data?.maxDate);
  const hasWatchOptions = useMemo(
    () =>
      data?.attributes?.[0]?.url ||
      [PERFORMANCE_DATE_MODE_TYPES.FULL_VIDEO, PERFORMANCE_DATE_MODE_TYPES.LIVESTREAM].includes(data?.mode) ||
      data?.stats?.media?.videos?.digital?.exists ||
      data?.performances?.some(performance =>
        [PERFORMANCE_DATE_MODE_TYPES.FULL_VIDEO, PERFORMANCE_DATE_MODE_TYPES.LIVESTREAM].includes(performance?.mode),
      ),
    [data],
  );

  const ticketUrl = useMemo(() => getTicketsURL(data), [data]);
  const hasFuturePerformancesDates = useMemo(
    () => createDate().isSame(productionMaxDate, 'day') || productionMaxDate.isAfter(createDate(), 'day'),
    [productionMaxDate],
  );

  let actionType = 'options';

  if (ticketUrl) {
    actionType = 'tickets';
  } else if (hasWatchOptions) {
    actionType = 'watch';
  }

  return (
    <div className={classes.actions}>
      <SecondaryActions
        entity={entity}
        entityType={entityType}
        trackingData={trackingData}
        isTextButton={isTextButton}
        hasFuturePerformancesDates={hasFuturePerformancesDates}
        actionType={actionType}
        data={data}
        variant={variant}
      />
    </div>
  );
};

export default Actions;
